import type { CampaignSimpleResponse } from '@coorum-research/shared-common/api-response/popup.schema'
import type { CampaignSelectionConfig } from '@coorum-research/shared-common/types'
import type { ThemeConfig } from '@coorum-research/shared-common/types/theme.type'
import { clsx } from 'clsx/lite'
import campaignDefault01 from '../media/campaignDefault01.png'
import campaignDefault02 from '../media/campaignDefault02.png'
import campaignDefault03 from '../media/campaignDefault03.png'
import { coorumColors } from '../survey/coorumTheme'
import { cn } from '../utils'
import { ThemeContext } from './ThemeContext'

/**
 * デフォルト画像
 * TODO: デフォルト画像選択機能ができたら、機能を置き換える
 */
const defaultImages = [
  campaignDefault01,
  campaignDefault02,
  campaignDefault03,
] as const

/**
 * 画像のidxからデフォルト画像を選んでURLを返す
 * TODO: デフォルト画像選択機能ができたら、機能を置き換える
 * @param idx
 * @returns image url
 */
function chooseDefaultImage(idx: number) {
  let imageHost = ''
  if (import.meta.env.PROD && import.meta.env.VITE_WIDGET_CDN_HOST) {
    imageHost = import.meta.env.VITE_WIDGET_CDN_HOST
  }
  const img = defaultImages[idx % defaultImages.length]
  return `${imageHost}${img}`
}

function CampaignSelectionButton({
  idx,
  campaign,
  title,
  hideCampaignImageText,
  onSelected,
}: {
  idx: number
  campaign: CampaignSimpleResponse
  title: string
  hideCampaignImageText?: boolean
  onSelected: (campaignId: string, campaignName: string) => void
}) {
  const imgSrc = campaign.assetConfigJson?.imageUrl ?? chooseDefaultImage(idx)

  return (
    <button
      type="button"
      onClick={() => onSelected(campaign.id, campaign.name)}
      className="block"
    >
      <img
        src={imgSrc}
        className="aspect-[2/1] w-full rounded-md border border-[#E2E3E2] object-cover"
        alt={`${title}キャンペーン画像`}
      />
      {!hideCampaignImageText && (
        <div className="mt-1 text-center font-medium">
          {campaign.surveyName || campaign.name}
        </div>
      )}
    </button>
  )
}

type Props = {
  theme?: ThemeConfig
  contentConfig?: CampaignSelectionConfig
  campaigns: CampaignSimpleResponse[]
  onSelected: (campaignId: string, campaignName: string) => void
}

export function CampaignSelectionComponent({
  theme,
  contentConfig,
  campaigns,
  onSelected,
}: Props) {
  const ariaTitle = contentConfig?.headingContent || '回答するアンケートを選択してください'

  return (
    <ThemeContext.Provider value={{ ...coorumColors, ...theme }}>
      <div className="flex h-full flex-col gap-4 overflow-y-auto p-6">
        <div className="flex w-full flex-col gap-4 break-all">
          {contentConfig?.headingContent && (
            <h3
              className={cn(
                'w-full text-2xl font-bold',
                contentConfig?.headingContentAlign === 'center' && 'text-center',
              )}
            >
              {contentConfig?.headingContent}
            </h3>
          )}

          {contentConfig?.headingContentImageUrl && (
            <img
              src={contentConfig.headingContentImageUrl}
              className="w-full rounded-md object-cover"
              alt={`${ariaTitle}タイトル画像`}
            />
          )}
          {contentConfig?.paragraphContent && (
            <p className="whitespace-pre-wrap font-medium">
              {contentConfig.paragraphContent}
            </p>
          )}
        </div>
        <div
          className={clsx(
            'grid items-start gap-2',
            contentConfig?.displayColumnSize === 'one' ? 'grid-cols-1' : 'grid-cols-2',
          )}
        >
          {campaigns.map((campaign, idx) => (
            <CampaignSelectionButton
              key={campaign.id}
              idx={idx}
              campaign={campaign}
              title={ariaTitle}
              hideCampaignImageText={contentConfig?.hideCampaignImageText}
              onSelected={onSelected}
            />
          ))}
        </div>
      </div>
    </ThemeContext.Provider>
  )
}
